import React from "react";
import { useTranslation } from "react-i18next";

import { AIStandardSuggestion } from "./types";

interface SuggestionCardProps {
  suggestion: AIStandardSuggestion;
  isSelected: boolean;
  onAdd: (suggestion: AIStandardSuggestion) => void;
  onDismiss: (standardId: string) => void;
}

export const SuggestionCard = ({
  suggestion,
  isSelected,
  onAdd,
  onDismiss,
}: SuggestionCardProps) => {
  const { t } = useTranslation();

  const relevancePercent = Math.round(suggestion.relevance_score * 100);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2 mb-1">
            <span className="text-sm font-semibold text-gray-900">
              {suggestion.standard_id}
            </span>
            {suggestion.standard_version && (
              <span className="text-xs text-gray-500">
                ({suggestion.standard_version})
              </span>
            )}
            {suggestion.directive && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-cyan-100 text-cyan-800">
                {suggestion.directive.toUpperCase()}
              </span>
            )}
            {suggestion.type && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                {t("standards.search.table.type")} {suggestion.type}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-700 mb-2">{suggestion.title}</p>
          <p className="text-sm text-gray-500 mb-2">
            {suggestion.relevance_explanation}
          </p>
          <p className="text-xs text-gray-400">
            {t("standards.aiResearch.relevance")}: {relevancePercent}%
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        {isSelected ? (
          <span className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-md">
            <svg
              className="w-4 h-4 mr-1.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            {t("standards.aiResearch.alreadyAdded")}
          </span>
        ) : (
          <button
            onClick={() => onAdd(suggestion)}
            type="button"
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
          >
            {t("standards.aiResearch.addStandard")}
          </button>
        )}
        <button
          onClick={() => onDismiss(suggestion.standard_id)}
          type="button"
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
        >
          {t("standards.aiResearch.dismiss")}
        </button>
      </div>
    </div>
  );
};
